import type { BuildStatus, EntitlementsSnapshot, ModelEntry, TierId } from "../../services/api/schemas";
import type { Session } from "../../services/auth/port";

/**
 * The access rule. This is the logic that protects revenue, so it lives in one
 * pure function with an exhaustive test rather than scattered across components.
 *
 * ⚠️ It is also NOT security. It decides what the UI shows. The server must
 * re-check entitlement on every request regardless — anyone with a valid token
 * and curl bypasses everything in this file.
 */

export type UpsellOption =
  | { kind: "model"; modelId: ModelEntry["id"]; priceId: string; monthlyPriceAud: number }
  | { kind: "tier"; tier: Exclude<TierId, "free"> };

export type AccessDecision =
  | { allowed: true; via: "pro" | "premium" | "model-sub" }
  | { allowed: false; reason: "not-built"; status: BuildStatus }
  | { allowed: false; reason: "signed-out" }
  | { allowed: false; reason: "not-entitled"; upsell: UpsellOption[] };

export function canAccess(
  model: ModelEntry,
  session: Session | null,
  ents: EntitlementsSnapshot | null,
): AccessDecision {
  /* REALITY GATES POLICY, and the ordering is the design.
     A Pro subscriber does not get NFL Sport, because NFL Sport does not exist.
     Build status is checked first and cannot be bought past. */
  if (model.build === "soon" || model.build === "off-roadmap") {
    return { allowed: false, reason: "not-built", status: model.build };
  }

  if (!session) return { allowed: false, reason: "signed-out" };

  if (ents?.tier === "pro") return { allowed: true, via: "pro" };
  if (ents?.tier === "premium") return { allowed: true, via: "premium" };
  if (ents?.modelSubs.includes(model.id)) return { allowed: true, via: "model-sub" };

  return { allowed: false, reason: "not-entitled", upsell: upsellsFor(model) };

  /* Deliberately NOT checked here: credits.
     Guardrails §6.3 requires a zero balance to be a graceful in-chat moment —
     "a warm line + a cached nudge + an upgrade CTA, never a hard cutoff". If
     access were gated on credits, a broke user would be bounced to /pricing
     instead, and we'd have broken a binding requirement with a routing decision.
     Credit state is handled inside the chat surface. */
}

export function upsellsFor(model: ModelEntry): UpsellOption[] {
  const out: UpsellOption[] = [];
  if (model.purchasable && model.priceId && model.monthlyPriceAud !== undefined) {
    out.push({
      kind: "model",
      modelId: model.id,
      priceId: model.priceId,
      monthlyPriceAud: model.monthlyPriceAud,
    });
  }
  out.push({ kind: "tier", tier: "premium" }, { kind: "tier", tier: "pro" });
  return out;
}

/**
 * Credit state — separate from access on purpose (see above).
 * `atZero` drives the §6.3 panel; it never drives a redirect.
 */
export function creditState(ents: EntitlementsSnapshot | null) {
  const balance = ents?.credits.balance ?? 0;
  return {
    balance,
    atZero: balance <= 0,
    /* Advisory only. The client's snapshot is always stale, so the UI must still
       handle a 402 even when this says there's headroom. */
    low: balance > 0 && balance <= 10,
    resetAt: ents?.credits.resetAt,
  };
}
